<template>
  <div class="automation-result-overview">
    <dd-header-card header="Verwerken met aangepaste verzendlijst">
      <template v-slot:subtitle>{{ automation.title }}</template>
      <div class="flex-col gap-y-md">
        <div>
          <dd-input v-model="name" title="Zoek op voor- of achternaam"/>
        </div>

        <dd-loading-indicator v-if="loading" message="Laden..."></dd-loading-indicator>

        <dd-main-table
            v-if="!loading"
            :headers="tableHeaders"
            :rows="automationResults"
            :initial-load-page-object="loadPageObject"
            :paginator="pagination"
            :loading="loading"
            @update-deselected="onUpdateDeselected"
            @update-selected="onUpdateSelected"
            @load-page="setPageObjectAndFetchResults"
            :rounded="true"
            :selectReverse="true"
        >
        </dd-main-table>
        <div class="flex-row gap-sm justify-content-between align-items-center">
          <dd-button @click="$emit('hide-list')" type="danger" :outline="true">
            Annuleren
          </dd-button>
          <a-popconfirm
              :title="confirmText"
              ok-text="Verwerken"
              cancel-text="Annuleer"
              @confirm="onProcessClick"
          >
            <dd-button>
              Aangepast uitvoeren
            </dd-button>
          </a-popconfirm>
        </div>
      </div>
    </dd-header-card>
  </div>
</template>

<script>
import { Skeleton } from "vue-loading-skeleton";
import { mapActions, mapGetters, mapMutations } from "vuex";
import store from "~/pages/automation/store";
import AutomationResultRowComponent from "./AutomationResultRowComponent.vue";
import EmailCheckOutline from "vue-material-design-icons/EmailCheckOutline.vue";
import CloseThick from "vue-material-design-icons/CloseThick.vue";
import CheckBold from "vue-material-design-icons/CheckBold.vue";
import CustomerBadgeComponent from "~/modules/customer/components/BadgeComponent.vue";
import { DateFormatEnum } from "~/enums";
import CustomerAvatarCell from "~/new_design_components/tables/cell_types/CustomerAvatarCell.vue";

export default {
  name: "AutomationResultOverviewComponent",
  components: {
    CustomerBadgeComponent,
    CheckBold,
    CloseThick,
    EmailCheckOutline,
    CustomerAvatarCell,
    Skeleton,
    AutomationResultRowComponent,
  },
  props: {
    automation: {
      type: Object,
      default: null,
    },
    showAutomation: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: true,
      loaded: false,
      name: '',
      deselected: [],
      selected: [],
      tableHeaders: [
        {
          title: "Klant",
          typeComponent: CustomerAvatarCell,
          key: (automationResult) => automationResult.customer?.data
        },
        {
          title: "Datum",
          width: '100px',
          key: (automationResult) => moment(automationResult.created_at).format(DateFormatEnum.DISPLAY_DATE),
        },
        {
          title: "Tijd",
          width: '100px',
          key: (automationResult) => moment(automationResult.created_at).format(DateFormatEnum.TIME),
        },
      ],
    };
  },
  mounted() {
    const STORE_KEY = "$_automation";
    // eslint-disable-next-line no-underscore-dangle
    if (!(STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(STORE_KEY, store);
    }
    this.fetchResults();
  },
  watch: {
    name() {
      this.fetchResults();
    },
  },
  computed: {
    ...mapGetters('$_automation', {
      me: "me",
      pagination: "automationResultsPagination",
      automationResults: "automationResults",
      hasAutomationResults: "hasAutomationResults",
      selectedAutomationResults: "selectedAutomationResults",
      ignoredAutomationResults: "ignoredAutomationResults",
      hasSelectedAutomationResults: "hasSelectedAutomationResults",
    }),
    infoColorClass() {
      return this.primary ? "bg-color-white-transparent" : "bg-color-primary-transparent";
    },
    isEmpty() {
      return this.automationResults.length === 0 && this.loaded;
    },
    loadPageObject() {
      return this.pagination || {
        page: 1,
        perPage: 10,
      }
    },
    confirmText() {
      let singleOrMultiple = this.ignoredAutomationResults.length > 1 ? 'resultaten' : 'resultaat';
      return `Weet je zeker dat je ${this.ignoredAutomationResults.length} ${singleOrMultiple} wil negeren?`;
    }
  },
  methods: {
    ...mapMutations('$_automation', {
      setIgnoredAutomationResults: 'SET_IGNORED_AUTOMATION_RESULTS',
      setSelectedAutomationResults: 'SET_SELECTED_AUTOMATION_RESULTS',
      setAutomationResultsPagination: 'SET_AUTOMATION_RESULTS_PAGINATION',
    }),
    ...mapActions('$_automation', {
      fetchMe: "fetchMe",
      fetchAutomationResults: "fetchAutomationResults",
      fetchAutomationResultsByAutomation: "fetchAutomationResultsByAutomation",
      fetchAutomationResultsByGlobalAutomationConfiguration: "fetchAutomationResultsByGlobalAutomationConfiguration",
      selectAutomationResult: "selectAutomationResult",
      isAutomationResultSelected: "isAutomationResultSelected",
      manuallyProcessAutomationResult: "manuallyProcessAutomationResult",
      ignoreAutomationResult: "ignoreAutomationResult",
      manuallyProcessSelectedAutomationResults: "manuallyProcessSelectedAutomationResults",
      ignoreSelectedAutomationResults: "ignoreSelectedAutomationResults",
      processUnprocessedByAutomation: "processUnprocessedByAutomation",
      paginateUnprocessedResultsByAutomation: "paginateUnprocessedResultsByAutomation",
      paginateUnprocessedResultsByGlobalAutomationConfiguration: "paginateUnprocessedResultsByGlobalAutomationConfiguration",
    }),
    onUpdateDeselected(deselected) {
      this.setIgnoredAutomationResults(deselected);
    },
    onUpdateSelected(selected) {
      this.setSelectedAutomationResults(selected);
    },
    fetchResults() {
      const loadingPromises = [
        this.fetchMe(),
      ];

      if (this.automation !== null) {
        if (!('is_global' in this.automation) || this.automation.is_global === false) {
          loadingPromises.push(
              this.paginateUnprocessedResultsByAutomation({
                automation: this.automation,
                page: this.loadPageObject.page,
                perPage: this.loadPageObject.perPage,
                name: this.name,
              })
          );
        } else if ('is_global' in this.automation && this.automation.is_global === true) {
          loadingPromises.push(
              this.paginateUnprocessedResultsByGlobalAutomationConfiguration(
                  {
                    globalAutomationConfiguration: this.automation,
                    page: this.loadPageObject.page,
                    perPage: this.loadPageObject.perPage,
                    name: this.name,
                  },
              )
          );
        }
      }

      Promise.all(loadingPromises)
          .finally(() => {
            this.loading = false;
            this.loaded = true;
          });
    },
    setPageObjectAndFetchResults(loadPageObject) {
      this.setAutomationResultsPagination(loadPageObject);
      this.fetchResults();
    },
    date(automationResult) {
      return moment(automationResult.created_at).format("DD-MM-YYYY");
    },
    time(automationResult) {
      return moment(automationResult.created_at).format("HH:mm");
    },
    onProcessClick() {
      this.ignoreSelectedAutomationResults().then(() => {
        this.processUnprocessedByAutomation(this.automation?.id).then(() => {
          this.$emit('done');
        });
      });
    },
  },
}
</script>

<style scoped lang="scss">
.automation-result-overview {
  .row {
    border-radius: 5px;
    padding: 5px;
  }
  .ant-select {
    width: 100% !important;
    display: block !important;
  }
}
</style>
